import { useMemo, type CSSProperties } from "react";
import { Component, Viewport, CanvasSettings } from "@/types";
import { normalizeLegacyCanvasBackgroundColor } from "@/utils/theme-aware-legacy-colors";

// Generate canvas background styles
export const useCanvasBackgroundStyle = (
  settings?: CanvasSettings
): CSSProperties => {
  return useMemo(() => {
    const background = settings?.background;
    if (!background) return {};

    const baseStyle: CSSProperties = {};

    switch (background.type) {
      case "color":
        baseStyle.backgroundColor = normalizeLegacyCanvasBackgroundColor(background.color);
        break;

      case "gradient":
        if (background.gradient) {
          const { type, direction, stops } = background.gradient;
          const gradient =
            type === "radial" ? "radial-gradient" : "linear-gradient";
          const stopsStr =
            stops
              ?.map((stop) => `${stop.color} ${stop.position}%`)
              .join(", ") || `${background.color} 0%, ${background.color} 100%`;
          baseStyle.background = `${gradient}(${direction || "0deg"}, ${stopsStr})`;
        } else {
          baseStyle.backgroundColor = background.color;
        }
        break;

      case "pattern":
        if (background.pattern) {
          const { type, color, size, spacing, opacity } = background.pattern;
          baseStyle.backgroundColor = background.color;

          let patternImage = "";
          switch (type) {
            case "dots":
              patternImage = `radial-gradient(circle at center, ${color} ${
                size / 2
              }px, transparent ${size / 2}px)`;
              break;
            case "lines":
              patternImage = `repeating-linear-gradient(0deg, transparent, transparent ${
                spacing / 2
              }px, ${color} ${spacing / 2}px, ${color} ${spacing / 2 + size}px)`;
              break;
            case "grid":
              patternImage = `
                linear-gradient(to right, ${color} 1px, transparent 1px),
                linear-gradient(to bottom, ${color} 1px, transparent 1px)
              `;
              break;
            case "diagonal":
              patternImage = `repeating-linear-gradient(45deg, transparent, transparent ${spacing}px, ${color} ${spacing}px, ${color} ${
                spacing + size
              }px)`;
              break;
          }

          if (patternImage) {
            baseStyle.backgroundImage = patternImage;
            baseStyle.backgroundSize = `${spacing}px ${spacing}px`;
            baseStyle.opacity = opacity;
          }
        } else {
          baseStyle.backgroundColor = background.color;
        }
        break;

      case "image":
        if (background.image) {
          const { url, size, position, repeat, opacity } = background.image;
          baseStyle.backgroundImage = `url(${url})`;
          baseStyle.backgroundSize = size;
          baseStyle.backgroundPosition = position;
          baseStyle.backgroundRepeat = repeat;
          baseStyle.opacity = opacity;
        }
        break;
    }

    return baseStyle;
  }, [settings?.background]);
};

// Generate canvas layout styles
export const useCanvasLayoutStyle = (
  settings?: CanvasSettings
): CSSProperties => {
  return useMemo(() => {
    const layout = settings?.layout;
    if (!layout) return {};

    const layoutStyle: CSSProperties = {
      padding: layout.padding
        ? `${layout.padding.top}px ${layout.padding.right}px ${layout.padding.bottom}px ${layout.padding.left}px`
        : undefined,
      maxWidth: layout.maxWidth || undefined,
      maxHeight: layout.maxHeight || undefined,
      overflow: layout.overflow,
      margin: layout.centerContent ? "0 auto" : undefined,
    };

    return layoutStyle;
  }, [settings?.layout]);
};

// Get viewport dimensions - matching editor's actual dimensions
export const getViewportDimensions = (viewport: Viewport) => {
  switch (viewport) {
    case "mobile":
      return { width: 375, height: 667 };
    case "tablet":
      return { width: 768, height: 1024 };
    case "desktop":
    default:
      return { width: 1920, height: 1080 };
  }
};

// Calculate total content height for scrolling
export const useContentHeight = (components: Component[]): number => {
  return useMemo(() => {
    if (components.length === 0) return window.innerHeight;

    let maxBottom = 0;
    components.forEach((component) => {
      if (!component.position || !component.size) {
        return;
      }

      const height = typeof component.size.height === "number" ? component.size.height : 0;

      if (height === 0) {
        return;
      }

      const bottom = (component.position.y ?? 0) + height;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });

    const minHeight = window.innerHeight - 64;
    const calculatedHeight = Math.max(maxBottom + 50, minHeight);

    return calculatedHeight;
  }, [components]);
};
